import React, { useContext, useState } from 'react';
import { Button, Card, Checkbox, Notification, toast } from 'components/ui';
import { HiOutlinePencil, HiOutlineTrash, HiOutlinePhotograph } from 'react-icons/hi';
import MySocialIcon from 'components/shared/MySocialIcon';
import ConfirmDialog from 'components/shared/ConfirmDialog';
import { PostArticleContext } from 'contexts/PostArticleContext';
import { SessionContext } from 'contexts/SessionContext';
import { stripHTML } from 'utils/stripHTML';
import { formatInUserTz } from 'utils/timezone';
import PostArticleButton from './components/PostArticleButton';
import { usePostGroupActions } from './utils/usePostGroupActions';

const STATUS_PILL = {
    published: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300',
    publishing: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300',
    error: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300',
    pending: 'bg-primary-100 text-primary-700 dark:bg-primary-500/20 dark:text-primary-300',
};

const getThumbnail = (asset) => {
    const imageUrl = asset?.image?.thumbnail?.url || asset?.image?.url;
    if (imageUrl) return { url: imageUrl, isVideo: false };
    const videoThumb = asset?.video?.thumbnail?.url;
    if (videoThumb) return { url: videoThumb, isVideo: false };
    const videoUrl = asset?.video?.url;
    if (videoUrl) return { url: videoUrl, isVideo: true };
    return null;
};

/**
 * Compact horizontal card for a post group — used by the /posts list view.
 * Shows stacked media, title/description, social networks, status, and the
 * edit / delete / publish actions to the right.
 */
const PostListItem = ({
    item,
    selectionMode = false,
    selected = false,
    onToggleSelect = () => void 0,
    onRemovePost = undefined,
    updatePost = undefined,
}) => {
    const { user } = useContext(SessionContext);
    const { openPostArticle } = useContext(PostArticleContext);
    const { status, isLoading, onPostNow, updateFieldValuePostGroup, onDeletePostGroup } =
        usePostGroupActions(item, { onUpdated: updatePost });

    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const posts = item?.posts || [];
    const networks = [...new Set(posts.map((p) => p?.social_network_type).filter(Boolean))];

    const thumbnails = Array.from(
        posts
            .flatMap((p) => p?.assets || [])
            .map(getThumbnail)
            .filter(Boolean)
            .reduce((map, thumb) => {
                if (!map.has(thumb.url)) map.set(thumb.url, thumb);
                return map;
            }, new Map())
            .values()
    );

    const primaryText = stripHTML(posts.find((p) => p?.description)?.description || '').trim();
    const realTitle = item?.title?.trim();
    const primaryLine = realTitle || primaryText || 'Untitled post';
    const secondaryLine = realTitle ? primaryText : '';

    const statusLabel =
        status == 'published'
            ? 'Published'
            : status == 'publishing'
            ? 'Publishing'
            : status == 'error'
            ? 'Error'
            : item?.draft
            ? 'Draft'
            : item?.publish_at
            ? 'Scheduled'
            : 'Pending';

    const handleRowClick = () => {
        if (selectionMode) onToggleSelect(item);
    };

    const handleEdit = (e) => {
        e?.stopPropagation();
        openPostArticle(item);
    };

    const handleConfirmDelete = async () => {
        if (isDeleting) return;
        setIsDeleting(true);
        try {
            await onDeletePostGroup();
            toast.push(<Notification title="Post deleted" type="success" />);
            onRemovePost?.(item);
        } catch (err) {
            toast.push(<Notification title={err?.message || 'Could not delete the post'} type="danger" />);
        }
        setIsDeleting(false);
        setConfirmDeleteOpen(false);
    };

    return (
        <>
            <Card
                clickable={selectionMode}
                onClick={handleRowClick}
                bodyClass="!p-0"
                className={`p-2 border transition-colors duration-150 rounded-xl bg-secondary-500 dark:bg-gray-800 ${
                    selected
                        ? 'border-primary-400 dark:border-primary-500 bg-primary-50/40 dark:bg-primary-500/5'
                        : 'border-gray-100 dark:border-gray-700/60 hover:border-gray-200 dark:hover:border-gray-600'
                }`}
            >
                <div className="flex items-center gap-3">
                    {selectionMode && (
                        <Checkbox checked={selected} readOnly className="shrink-0 pointer-events-none ml-1" />
                    )}

                    {/* Stacked media */}
                    <div className="flex shrink-0 items-center">
                        {thumbnails.length > 0 ? (
                            thumbnails.slice(0, 3).map((thumb, i) => (
                                <div
                                    key={thumb.url}
                                    className="w-11 h-11 rounded-lg overflow-hidden ring-2 ring-secondary-500 dark:ring-gray-800 bg-gray-100 dark:bg-gray-700"
                                    style={{ marginLeft: i === 0 ? 0 : -14, zIndex: 3 - i }}
                                >
                                    {thumb.isVideo ? (
                                        <video
                                            src={thumb.url}
                                            className="w-full h-full object-cover"
                                            muted
                                            playsInline
                                            preload="metadata"
                                        />
                                    ) : (
                                        <img src={thumb.url} alt="" className="w-full h-full object-cover" />
                                    )}
                                </div>
                            ))
                        ) : (
                            <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-gray-100 dark:bg-gray-700/60 text-gray-300 dark:text-gray-500">
                                <HiOutlinePhotograph className="text-xl" />
                            </div>
                        )}
                        {thumbnails.length > 3 && (
                            <div
                                className="w-11 h-11 rounded-lg flex items-center justify-center ring-2 ring-secondary-500 dark:ring-gray-800 bg-gray-100 dark:bg-gray-700 text-[11px] font-semibold text-gray-500 dark:text-gray-300"
                                style={{ marginLeft: -14 }}
                            >
                                +{thumbnails.length - 3}
                            </div>
                        )}
                    </div>

                    {/* Title / description / networks */}
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                            <p
                                className={`truncate text-[13px] leading-snug ${
                                    realTitle
                                        ? 'font-medium text-gray-800 dark:text-gray-100'
                                        : 'font-normal text-gray-600 dark:text-gray-300'
                                }`}
                            >
                                {primaryLine}
                            </p>
                            <span
                                className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium ${STATUS_PILL[status]}`}
                            >
                                {statusLabel}
                            </span>
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                            <div className="flex items-center">
                                {networks.map((network, i) => (
                                    <MySocialIcon
                                        key={network}
                                        network={network}
                                        className="!h-4 !w-4 rounded-full"
                                        wrapperClassName="shrink-0"
                                        style={{ marginLeft: i === 0 ? 0 : -3 }}
                                    />
                                ))}
                            </div>
                            {!!secondaryLine && (
                                <span className="text-[11px] text-gray-400 dark:text-gray-500 truncate">
                                    {secondaryLine}
                                </span>
                            )}
                            {item?.publish_at && (
                                <span className="shrink-0 text-[11px] text-gray-400 dark:text-gray-500">
                                    {formatInUserTz(item.publish_at, user, 'MMM D · h:mm a')}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div
                        className={`flex items-center gap-1 shrink-0 ${
                            selectionMode ? 'opacity-40 pointer-events-none' : ''
                        }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <PostArticleButton
                            postGroup={item}
                            status={status}
                            onSave={handleEdit}
                            onPostNow={onPostNow}
                            updateFieldValuePostGroup={updateFieldValuePostGroup}
                            isLoading={isLoading}
                            size="sm"
                        />
                        <Button
                            size="sm"
                            variant="plain"
                            icon={<HiOutlinePencil />}
                            onClick={handleEdit}
                        />
                        <Button
                            size="sm"
                            variant="plain"
                            color="red-600"
                            icon={<HiOutlineTrash />}
                            onClick={(e) => {
                                e.stopPropagation();
                                setConfirmDeleteOpen(true);
                            }}
                        />
                    </div>
                </div>
            </Card>

            <ConfirmDialog
                isOpen={confirmDeleteOpen}
                onClose={() => setConfirmDeleteOpen(false)}
                type="danger"
                title="Delete post"
                cancelText="Cancel"
                confirmText={isDeleting ? 'Deleting...' : 'Delete'}
                confirmButtonColor="red-600"
                onCancel={() => setConfirmDeleteOpen(false)}
                onConfirm={handleConfirmDelete}
            >
                <p>
                    Are you sure you want to delete this post? This action cannot be undone.
                </p>
            </ConfirmDialog>
        </>
    );
};

export default PostListItem;
